import { useCallback, useEffect, useState } from "react";
import useAxiosPrivate from "./useAxiosPrivate";
import API_URLS from "../constants/apiConstants";
import { getMainGroupMember } from "../utils/messagesUtils";
import useAuth from "./useAuth";
import { useInboxWebSocket } from "./useInboxWebSocket";

export const useInboxData = () => {
  const { auth, school } = useAuth();
  const NURSERY_ID = school.id;
  const axiosPrivate = useAxiosPrivate();
  const { isConnected, onMessage } = useInboxWebSocket();
  const [paginationData, setPaginationData] = useState({
    "admin-staff": { currentPage: 1, totalPages: 1, totalItems: 0, next: null, previous: null },
    student:       { currentPage: 1, totalPages: 1, totalItems: 0, next: null, previous: null },
    "admin-student":{ currentPage: 1, totalPages: 1, totalItems: 0, next: null, previous: null },
  });
  const [inboxes, setInboxes] = useState({
    "admin-staff": null,
    student: null,
    "admin-student": null,
  });

  const [newReceivedMessage, setNewReceivedMessage] = useState(null);
  const [inboxesLoaded, setInboxesLoaded] = useState({});
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [inboxUnreadCounts, setInboxUnreadCounts] = useState({
    "admin-staff": 0,
    student: 0,
    "admin-student": 0,
  });

  const authorIsCurrentUser = useCallback(
    (member) => {
      return member.type === auth.role && member.id === auth.id;
    },
    [auth.id, auth.role]
  );

  const fetchInbox = useCallback(async (groupType, page = 1) => {
    try {
      const api_url = API_URLS.getAdminInbox(NURSERY_ID, groupType, page); // pass page
      const response = await axiosPrivate.get(api_url);
      const { results, total_pages, total_items, links, current_page } = response.data;

      const transformedGroups = results.map((group) => ({
        ...group,
        main_member: getMainGroupMember(auth.role, groupType, group.main_members),
      }));

      // Save pagination meta per inbox
      setPaginationData((prev) => ({
        ...prev,
        [groupType]: {
          currentPage: current_page,
          totalPages: total_pages,
          totalItems: total_items,
          next: links.next,
          previous: links.previous,
        },
      }));

      return transformedGroups;
    } catch (error) {
      return null;
    }
  }, [NURSERY_ID, auth.role, axiosPrivate]);

  // Page change handler (call this from UI)
  const changeInboxPage = useCallback(async (groupType, page) => {
    const data = await fetchInbox(groupType, page);
    if (data) {
      setInboxes((prev) => ({ ...prev, [groupType]: data }));
    }
  }, [fetchInbox]);

  const fetchAllInboxes = useCallback(async () => {
    const [staff, parents, privateInbox] = await Promise.all([
      fetchInbox("admin-staff"),
      fetchInbox("student"),
      fetchInbox("admin-student"),
    ]);

    setInboxes({
      "admin-staff": staff,
      student: parents,
      "admin-student": privateInbox,
    });

    setInboxesLoaded({
      "admin-staff": true,
      student: true,
      "admin-student": true,
    });
  }, [fetchInbox]);

  const updateInbox = useCallback((groupType, updater) => {
    setInboxes((prev) => ({
      ...prev,
      [groupType]:
        typeof updater === "function" ? updater(prev[groupType]) : updater,
    }));
  }, []);

  // Fetch all inboxes
  useEffect(() => {
    if (isConnected) {
      fetchAllInboxes();
    }
  }, [isConnected, fetchAllInboxes]);

  // Handle incoming inbox messages (WebSocket)
  useEffect(() => {
    if (!isConnected) return;

    const handleNewMessage = (data) => {
      const newMessage = data.message;
      const groupType = data.group_type;
      setNewReceivedMessage(newMessage);

      updateInbox(groupType, (prevInboxes) => {
        if (!prevInboxes) return prevInboxes;

        const exists = prevInboxes.some((g) => g.group_name === data.group);
        if (!exists) return prevInboxes;

        const fromCurrentUser = authorIsCurrentUser(newMessage.author);
        return prevInboxes
          .map((group) => {
            if (group.group_name === data.group) {
              return {
                ...group,
                unread_count: fromCurrentUser
                  ? group.unread_count
                  : group.unread_count + 1,
                last_message: newMessage,
              };
            }
            return group;
          })
          .sort((a, b) => {
            const aTime = a.last_message?.created_at || a.created_at;
            const bTime = b.last_message?.created_at || b.created_at;
            return new Date(bTime) - new Date(aTime);
          });
      });
    };

    const unsubscribe = onMessage(handleNewMessage);
    return unsubscribe;
  }, [authorIsCurrentUser, isConnected, onMessage, updateInbox]);

  // Calculate unread counts whenever inboxes change
  useEffect(() => {
    const calculateUnreadCounts = () => {
      const counts = {
        "admin-staff": 0,
        student: 0,
        "admin-student": 0,
      };

      Object.entries(inboxes).forEach(([type, groups]) => {
        if (Array.isArray(groups)) {
          counts[type] = groups.reduce((sum, group) => sum + group.unread_count, 0);
        }
      });
      const total = Object.values(counts).reduce((sum, count) => sum + (count || 0), 0);

      setTotalUnreadCount(total);
      setInboxUnreadCounts(counts);
    };

    calculateUnreadCounts();
  }, [inboxes]); // This will recalculate whenever inboxes change

  return {
    inboxes,
    inboxesLoaded,
    updateInbox,
    totalUnreadCount,
    inboxUnreadCounts,
    newReceivedMessage,
    authorIsCurrentUser,
    paginationData,      
    changeInboxPage,     
  };
};
